import { getAugmentedSchema } from '@framework/form/field/getAugmentedSchema.ts';
import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { ArraySchema } from 'yup';

/**
 * Hook che restituisce una funzione per il calcolo del valore di default delle righe
 * di un widget lista ripetibile.
 *
 * Lo schema viene risolto on-demand sui valori correnti del form (non memoizzato)
 * così che eventuali `parentWhen` del repeatable padre o dei suoi field reagiscano
 * ai valori più recenti. Il cast riceve `validationContext` e `[VALIDATION_VALUES]`,
 * necessari per attivare `parentWhen` che dipendono dal context (es. default
 * dinamici o LR figlie min>0 materializzate ricorsivamente).
 */
export function useGetRepeatableChildrenDefault(name: string) {
  const augmented = useFormAugmentedContext();
  const methods = useFormContext();

  return useCallback(() => {
    const values = methods.getValues();
    const listSchema = getAugmentedSchema(augmented, values, name);
    if (!(listSchema instanceof ArraySchema)) return null;

    // Stiamo creando il default di un nuovo elemento della lista ripetibile
    // partendo da `{}`.
    // Per far valutare correttamente i `when`, passiamo anche il context del form:
    // `resolvedStore` contiene il risultato gia' calcolato di visibilita',
    // abilitazione e tag semantici dei campi; `VALIDATION_VALUES` aggiunge i
    // valori correnti del form, che non sarebbero presenti nell'oggetto vuoto.
    const context = {
      ...augmented?.resolvedStore.getContext(values),
    };

    return listSchema.innerType?.cast({}, { assert: false, context }) ?? null;
  }, [augmented, methods, name]);
}
